<script setup>
defineOptions({ name: "SolutionKaliShopPage" });
import { defineAsyncComponent, onMounted, onUnmounted, ref } from "vue";
import {
  GlobeAltIcon,
  TruckIcon,
  ShoppingCartIcon as ShoppingCartIconOutline,
  ChatBubbleLeftRightIcon,
  ArchiveBoxIcon,
  DevicePhoneMobileIcon as DevicePhoneMobileIconOutline,
  BoltIcon,
  UserGroupIcon,
  DocumentCheckIcon,
  ScaleIcon,
} from "@heroicons/vue/24/outline";
import {
  ShoppingCartIcon,
  WrenchScrewdriverIcon,
  SparklesIcon,
  ShoppingBagIcon,
  TrophyIcon,
  TagIcon,
  BookOpenIcon,
  PuzzlePieceIcon,
  HomeIcon,
  GiftIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/vue/24/solid";
import { useSeo } from "@/composables/useSeo";
import FaqPanel from "@/components/ui/FAQ.vue";
import SolutionHero from "@/components/ui/SolutionHero.vue";
import imageBg from "@/assets/solutions/kalishop.webp";
import visaoGeralImg from "@/assets/visaogeralshop.webp";
import gestaoEcommerce from "@/assets/gestao-ecommerce.webp";
import gestaoTributaria from "@/assets/gestao-tributaria.webp";

const CTA = defineAsyncComponent(() => import("@/components/home/CTA.vue"));
const Clients = defineAsyncComponent(() => import("@/components/home/Clients.vue"));

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};

const navLinks = [
  { label: "Visão geral", href: "#visao-geral" },
  { label: "Segmentos", href: "#segmentos" },
  { label: "Funcionalidades", href: "#funcionalidades" },
  { label: "Fiscal", href: "#fiscal" },
  { label: "Módulos", href: "#modulos" },
  { label: "Depoimentos", href: "#depoimentos" },
  { label: "Dúvidas", href: "#duvidas" },
];

const activeNav = ref(navLinks[0]?.href ?? "#visao-geral");
const setActiveNav = (href) => {
  activeNav.value = href;
};

const topOffsetPx = 140;
let scrollRaf = null;
const updateActiveFromScroll = () => {
  let current = navLinks[0]?.href ?? "#visao-geral";
  for (const link of navLinks) {
    const sectionEl = document.querySelector(link.href);
    if (!sectionEl) continue;
    const top = sectionEl.getBoundingClientRect().top;
    if (top - topOffsetPx <= 0) {
      current = link.href;
    } else {
      break;
    }
  }
  if (current !== activeNav.value) {
    setActiveNav(current);
  }
};

const onScroll = () => {
  if (scrollRaf) return;
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = null;
    updateActiveFromScroll();
  });
};

const handleNavClick = (event, href) => {
  event.preventDefault();
  setActiveNav(href);
  history.replaceState(null, "", href);
  const el = document.querySelector(href);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const syncActiveNavFromHash = () => {
  const hash = window.location.hash;
  if (!hash) return;
  if (navLinks.some((l) => l.href === hash)) {
    setActiveNav(hash);
  }
};

onMounted(() => {
  syncActiveNavFromHash();
  window.addEventListener("hashchange", syncActiveNavFromHash, {
    passive: true,
  });
  window.addEventListener("scroll", onScroll, { passive: true });
  updateActiveFromScroll();
});

onUnmounted(() => {
  window.removeEventListener("hashchange", syncActiveNavFromHash);
  window.removeEventListener("scroll", onScroll);
  if (scrollRaf) cancelAnimationFrame(scrollRaf);
});

// Conteúdo abaixo é baseado no ebook oficial "Kali Gestão Shop" (docs/books/KALI SHOP - EBOOK.pdf)
function openDemoForSegmento() {
  window.dispatchEvent(
    new CustomEvent("open-demo-modal", { detail: { segmento: "Loja virtual / E-commerce" } }),
  );
}

// e no conteúdo já existente na página. O Kali Shop é uma loja virtual: os segmentos abaixo são
// os mesmos tipos de varejo que já vendem com o ecossistema Kali, agora online.
const segmentos = [
  { icon: ShoppingCartIcon, label: "Mercados e alimentos" },
  { icon: WrenchScrewdriverIcon, label: "Autopeças e acessórios" },
  { icon: SparklesIcon, label: "Cosméticos e perfumarias" },
  { icon: ShoppingBagIcon, label: "Moda e acessórios" },
  { icon: TagIcon, label: "Calçados" },
  { icon: TrophyIcon, label: "Materiais esportivos" },
  { icon: GiftIcon, label: "Presentes e variedades" },
  { icon: BookOpenIcon, label: "Livrarias e papelarias" },
  { icon: PuzzlePieceIcon, label: "Brinquedos" },
  { icon: ComputerDesktopIcon, label: "Eletrônicos" },
  { icon: HomeIcon, label: "Móveis e decoração" },
  { icon: DevicePhoneMobileIcon, label: "Bijuterias e joalherias" },
];

const destaques = [
  {
    eyebrow: "Personalização",
    title: "Sua loja com a sua cara",
    text: "Crie páginas internas como \"Fale Conosco\" e \"Quem Somos\", cadastre parceiros e personalize os tipos de pagamento aceitos pelo seu negócio.",
    image: null,
    alt: "Personalização da loja virtual Kali Shop",
  },
  {
    eyebrow: "Catálogo",
    title: "Catálogo de produtos sob medida",
    text: "Crie atributos personalizados para cada item e organize os produtos em categorias, facilitando a navegação dos seus clientes.",
    image: null,
    alt: "Catálogo de produtos no Kali Shop",
  },
  {
    eyebrow: "Pedidos",
    title: "Controle total do negócio",
    text: "Gerencie todos os pedidos, personalize as etapas do processo de compra e mantenha o consumidor informado sobre o andamento da entrega.",
    image: gestaoEcommerce,
    alt: "Controle de pedidos no Kali Shop",
  },
  {
    eyebrow: "Logística",
    title: "Frete calculado automaticamente",
    text: "Integração completa com os Correios: cálculo automático de prazo e preço do frete, com valores específicos por faixa de CEP.",
    image: null,
    alt: "Cálculo de frete no Kali Shop",
  },
];

const documentosFiscais = ["NF-e", "NFC-e", "NFS-e", "CT-e", "CT-e OS", "MDF-e"];

const controleTributario = [
  { label: "Sped Fiscal", shade: "bg-kali-blue-700" },
  { label: "Bloco H", shade: "bg-kali-blue-600" },
  { label: "Bloco K", shade: "bg-kali-blue-500" },
  { label: "EFD Contribuições", shade: "bg-kali-blue-400" },
];

const modulos = [
  {
    icon: GlobeAltIcon,
    title: "Loja Virtual 24h",
    subtitle: "Venda a qualquer hora, de qualquer lugar",
    items: [
      "Design responsivo e personalizável",
      "Páginas internas próprias",
      "Importação de produtos via XML",
      "Segurança e conformidade com a LGPD",
    ],
  },
  {
    icon: ShoppingCartIconOutline,
    title: "Marketplaces",
    subtitle: "Venda em múltiplos canais",
    items: [
      "Integração com Mercado Livre",
      "Recebimento via Mercado Pago",
      "Catálogo sincronizado",
    ],
  },
  {
    icon: TruckIcon,
    title: "Frete e entregas",
    subtitle: "Cálculo automático com os Correios",
    items: [
      "Prazo e preço calculados automaticamente",
      "Valores específicos por faixa de CEP",
      "Acompanhamento do pedido pelo cliente",
    ],
  },
  {
    icon: ChatBubbleLeftRightIcon,
    title: "WhatsApp",
    subtitle: "Atendimento e pós-venda",
    items: [
      "Redirecionamento após o pedido",
      "Atendimento direto com o cliente",
      "Confirmações e acompanhamento",
    ],
  },
  {
    icon: ArchiveBoxIcon,
    title: "Controle de estoque",
    subtitle: "Em tempo real",
    items: [
      "Acompanhamento de entradas e saídas",
      "Sincronizado com os pedidos",
      "Etiquetas e curva ABC",
    ],
  },
  {
    icon: DevicePhoneMobileIconOutline,
    title: "SEUPOS",
    subtitle: "App de vendas online e offline",
    items: [
      "Compatível com Android e iOS",
      "Funciona sem internet",
      "Conectado à loja em tempo real",
    ],
  },
  {
    icon: BoltIcon,
    title: "CONNECT",
    subtitle: "Pagamento sem TEF tradicional",
    items: [
      "Mais ágil que o TEF convencional",
      "Menor custo operacional",
      "Integrado ao checkout da loja",
    ],
  },
  {
    icon: UserGroupIcon,
    title: "Painel do Contador",
    subtitle: "Documentos fiscais organizados",
    items: [
      "Acesso gratuito para o contador",
      "Documentos recebidos e enviados",
      "Comunicação facilitada",
    ],
  },
];

useSeo({
  title: "Sistema para Loja Virtual e E-commerce | Kali Shop",
  description:
    "Crie sua loja virtual com o Kali Shop. Integração com Mercado Livre, WhatsApp, estoque e pagamentos. Sistema completo para e-commerce.",
  image: imageBg,
  schema: {
    "@context": "https://schema.org",
    "@type": ["SoftwareApplication", "LocalBusiness"],
    name: "Kali Shop",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: "https://www.kalisistemas.com.br/solucao/kali-shop",
    description:
      "O Kali Shop é um sistema completo para lojas virtuais e e-commerces. Com ele, você cria sua loja online personalizada, gerencia produtos, pedidos, pagamentos e integra com os principais marketplaces e meios de pagamento. Tudo isso com controle fiscal, financeiro e estoque em tempo real.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "BRL",
    },
  },
});
</script>

<template>
  <div class="bg-white">
    <SolutionHero
      title="Kali Shop: Sistema de Gestão e Loja Virtual para Varejo Digital"
      description="Venda 24 horas por dia com uma loja virtual moderna, integrada e fácil de gerenciar. Controle pedidos, estoque, pagamentos e entregas com um sistema 100% online."
      primary-label="Solicite uma demonstração gratuita"
      primary-href="/contato?type=kali-shop"
      :background-image="imageBg"
      background-class="bg-top bg-cover bg-slate-900"
      overlay-variant="left"
    >
    </SolutionHero>

    <section
      class="sticky top-16 z-40 border-b border-gray-100 shadow-sm backdrop-blur bg-white/95"
    >
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="flex gap-6 items-center h-16">
          <nav
            class="flex overflow-x-auto relative gap-6 items-center h-16"
            aria-label="Navegação da página"
          >
            <a
              v-for="item in navLinks"
              :key="item.href"
              :href="item.href"
              class="menu-item-underline relative inline-flex items-center px-2 h-16 text-sm font-bold text-gray-700 hover:text-[#006086] transition-all duration-300 whitespace-nowrap"
              :class="{ 'text-[#006086] active': activeNav === item.href }"
              @click="(e) => handleNavClick(e, item.href)"
            >
              {{ item.label }}
            </a>
          </nav>
        </div>
      </div>
    </section>

    <!-- Visão geral -->
    <section id="visao-geral" class="overflow-hidden relative py-16 bg-white scroll-mt-24 lg:min-h-[620px]">
      <div class="hidden absolute inset-y-0 right-0 w-1/2 lg:block">
        <img
          :src="visaoGeralImg"
          alt="Kali Shop"
          class="object-cover object-center w-full h-full"
        />
      </div>
      <div class="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="lg:max-w-xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Sua loja virtual com a sua cara
          </h2>
          <p class="mt-6 text-lg leading-8 text-gray-600">
            O Kali Shop é um sistema completo para lojas virtuais e
            e-commerces. Ofereça aos seus clientes uma experiência prática e
            moderna, com uma loja ativa 24 horas por dia, integrada ao
            Mercado Livre, Mercado Pago, Correios e WhatsApp — tudo com
            controle fiscal, financeiro e estoque em tempo real.
          </p>
          <div class="flex gap-8 items-center mt-10 text-[#006086]">
            <DevicePhoneMobileIconOutline class="w-10 h-10" />
            <ComputerDesktopIcon class="w-10 h-10" />
          </div>
        </div>
      </div>
    </section>

    <!-- Segmentos -->
    <section id="segmentos" class="py-16 bg-gray-100 scroll-mt-24">
      <div class="grid gap-12 items-center px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:grid-cols-[1.3fr_1fr]">
        <div class="grid grid-cols-3 gap-x-6 gap-y-10 sm:grid-cols-4">
          <div
            v-for="s in segmentos"
            :key="s.label"
            v-reveal
            class="flex flex-col items-center text-center group"
          >
            <component
              :is="s.icon"
              class="w-11 h-11 text-kali-blue-600 transition-transform duration-200 ease-out group-hover:-translate-y-1"
            />
            <p
              class="mt-3 text-xs font-semibold text-gray-800 transition-colors duration-200 sm:text-sm group-hover:text-kali-blue-600"
            >
              {{ s.label }}
            </p>
          </div>
        </div>
        <div v-reveal>
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Soluções que atendem à realidade de cada
            <span class="text-kali-blue-600">loja</span>
            e operação
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            O Kali Shop se adapta a diferentes segmentos do varejo que
            querem vender online, sem abrir mão do controle fiscal,
            financeiro e de estoque que o restante da operação já tem.
          </p>
          <button
            type="button"
            @click="openDemoForSegmento"
            class="inline-flex justify-center items-center px-6 py-3 mt-6 text-base font-semibold text-white rounded-full border border-transparent shadow-sm bg-kali-blue-500 hover:bg-kali-blue-600"
          >
            Vendemos para diversos segmentos do varejo
          </button>
        </div>
      </div>
    </section>

    <!-- Funcionalidades em destaque -->
    <section id="funcionalidades" class="py-20 bg-gradient-to-br from-white via-gray-50 to-[#e6f0f4] scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2 class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-4">
            Sua loja pronta para vender em poucos minutos
          </h2>
          <p class="text-lg leading-8 text-gray-600">
            Da personalização ao frete, cada funcionalidade foi pensada para
            tirar trabalho manual da sua operação online.
          </p>
        </div>
        <div class="grid gap-8 mt-14 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="d in destaques" :key="d.title" v-reveal>
            <img
              v-if="d.image"
              :src="d.image"
              :alt="d.alt"
              class="object-cover w-full h-44 rounded-2xl"
            />
            <div
              v-else
              class="w-full h-44 rounded-2xl bg-gradient-to-br from-[#e6f0f4] to-[#b8dde8]"
            ></div>
            <p class="mt-4 text-xs font-bold tracking-wide text-[#006086] uppercase">
              {{ d.eyebrow }}:
            </p>
            <h3 class="mt-1 text-lg font-bold text-gray-900">{{ d.title }}</h3>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ d.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Reforma tributária -->
    <section class="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div
        v-reveal
        class="overflow-hidden relative bg-kali-blue-600 rounded-[3rem] shadow-xl"
      >
        <div class="grid gap-10 items-center px-8 py-12 lg:grid-cols-2 lg:px-16 lg:py-16">
          <div>
            <h2 class="text-2xl font-black tracking-tight text-white sm:text-3xl lg:text-4xl">
              Reforma tributária: é hora de se preparar!
            </h2>
            <p class="mt-4 text-base leading-7 text-kali-blue-100 sm:text-lg">
              O Kali Shop acompanha as mudanças da reforma tributária em
              tempo real, mantendo sua loja em conformidade sem parar de
              vender.
            </p>
            <a
              href="/reforma-tributaria"
              class="inline-flex justify-center items-center px-8 py-3 mt-8 text-sm font-bold tracking-wide text-kali-blue-700 uppercase bg-white rounded-full shadow-sm transition hover:bg-kali-blue-50"
            >
              Saiba mais
            </a>
          </div>
          <div class="hidden relative lg:block lg:h-80">
            <img
              :src="gestaoTributaria"
              alt="Especialistas em conformidade fiscal"
              class="object-cover absolute inset-0 w-4/5 h-full rounded-3xl shadow-lg"
            />
            <div
              class="flex absolute -top-6 left-4 justify-center items-center w-16 h-16 rounded-2xl shadow-lg bg-kali-gold"
            >
              <DocumentCheckIcon class="w-8 h-8 text-white" />
            </div>
            <div
              class="flex absolute -bottom-6 right-4 justify-center items-center w-20 h-20 bg-cyan-400 rounded-2xl shadow-lg"
            >
              <ScaleIcon class="w-9 h-9 text-white" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Fiscal -->
    <section id="fiscal" class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="grid gap-12 lg:grid-cols-2">
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Documentos fiscais
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Emita documentos fiscais direto da sua loja virtual, com
              importação de produtos via XML e vínculo automático à Receita
              Federal.
            </p>
            <div class="grid grid-cols-3 gap-3">
              <div
                v-for="d in documentosFiscais"
                :key="d"
                class="flex justify-center items-center py-4 text-lg font-bold rounded-lg bg-kali-blue-100 text-kali-blue-700"
              >
                {{ d }}
              </div>
            </div>
            <ul class="mt-6 space-y-2 text-sm text-gray-700">
              <li>
                <span class="font-semibold text-gray-900">Importação via XML —</span>
                importe produtos sem esforço, direto das notas fiscais.
              </li>
              <li>
                <span class="font-semibold text-gray-900">Segurança e LGPD —</span>
                validação de contas e e-mails, com total conformidade com a
                Lei Geral de Proteção de Dados.
              </li>
            </ul>
          </div>
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Controle fiscal e tributário
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Desenvolvido para MEI, ME, Simples, Lucro Real e Lucro
              Presumido, com emissão dos principais documentos fiscais.
            </p>
            <div class="grid grid-cols-2 gap-3">
              <div
                v-for="c in controleTributario"
                :key="c.label"
                class="flex items-end p-4 h-24 font-bold text-white rounded-lg"
                :class="c.shade"
              >
                {{ c.label }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Módulos -->
    <section id="modulos" class="py-16 bg-gradient-to-br from-white via-gray-50 to-[#e6f0f4] scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <h2
          class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-4"
        >
          Módulos do Kali Shop
        </h2>
        <p class="mt-4 mb-12 max-w-2xl text-lg leading-8 text-gray-600">
          Conecte o Kali Shop a módulos adicionais e leve a gestão da sua
          loja virtual a outro nível.
        </p>
        <div class="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="m in modulos" :key="m.title" v-reveal class="text-center">
            <component :is="m.icon" class="mx-auto w-10 h-10 text-[#006086]" />
            <h3 class="mt-4 text-lg font-bold text-gray-900">{{ m.title }}</h3>
            <p class="mt-1 text-sm font-semibold text-gray-700">{{ m.subtitle }}</p>
            <ul class="mt-4 space-y-2 text-sm text-gray-600">
              <li v-for="it in m.items" :key="it">{{ it }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Depoimentos -->
    <section id="depoimentos" class="scroll-mt-24">
      <Clients />
    </section>

    <section id="duvidas" class="scroll-mt-24">
      <FaqPanel
        :items="[
          {
            question: 'Integra com marketplaces?',
            answer:
              'Sim. Integração com principais marketplaces e meios de pagamento.',
          },
          {
            question: 'Controle de estoque e pedidos?',
            answer: 'Sim. Estoque em tempo real, pedidos e conciliação financeira.',
          },
          {
            question: 'Suporte a carrinho e promoções?',
            answer: 'Sim. Carrinho, cupons, campanhas e combos integrados.',
          },
          {
            question: 'Emite documentos fiscais?',
            answer: 'Sim. Emissão integrada conforme legislação.',
          },
        ]"
        description="Dúvidas sobre Kali Shop? Veja as respostas e fale com nosso time."
        cta-label="Falar com Consultor"
        :cta-opens-modal="true"
      />
    </section>

    <section id="fale-conosco" class="scroll-mt-16">
      <CTA />
    </section>
  </div>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
